import { parseAtBoundary } from './parse'
import { BoundaryParseError, BoundarySourceError } from './errors'
import type { ZodLikeSchema } from './zod-like-schema'

/**
 * HTTP trust-boundary primitive.
 *
 * Replaces the unsafe pattern:
 *   const data = await fetch(url).then(r => r.json()) as T   // ← lies at runtime
 *
 * With the parse-don't-validate pattern:
 *   const data = await httpBoundary({ name, url, schema: UserSchema })
 *   // data is guaranteed User — or BoundaryParseError was thrown
 *
 * Error contract:
 *   - Network failure / DNS / CORS preflight → BoundarySourceError
 *   - Deadline exceeded (headers or body never arrive) → BoundarySourceError
 *   - HTTP 4xx / 5xx (default) → BoundarySourceError, `cause: { status, body (capped 500), json (full, parsed) }`
 *   - Response body parses but does not match schema → BoundaryParseError
 *   - Response body is not valid JSON when JSON is expected → BoundaryParseError
 *
 * Status-code policy:
 *   The default treats any non-2xx as a source error. Callers that need to
 *   handle specific codes (e.g. 401 → redirect to login, 404 → render empty
 *   state) supply `onStatus` to intercept. The interceptor decides whether
 *   to throw a custom error, return a typed fallback, or let the default
 *   error flow continue. Critically: the interceptor runs BEFORE schema
 *   parsing, so 4xx error bodies (which never match the success schema) do
 *   not trip the parse path.
 */

export interface HttpBoundaryOptions<T> {
  /** Stable identifier for logs and L0-validator allowlist (e.g. "portal.users.get"). */
  name: string
  url: string
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  /** Schema that the 2xx response body must satisfy. Null = expect no body / void response. */
  schema: ZodLikeSchema<T> | null
  /** Request body. Auto-JSON-encoded; sets Content-Type accordingly. */
  body?: unknown
  headers?: Record<string, string>
  /** Forwarded to fetch — defaults to 'same-origin' to keep cookies. */
  credentials?: RequestCredentials
  /** Forwarded to fetch; callers handling credentials can reject redirects. */
  redirect?: RequestRedirect
  /**
   * Deadline in ms for the whole call (headers AND body). Defaults to
   * DEFAULT_HTTP_BOUNDARY_TIMEOUT_MS. Must be a positive finite number —
   * an unbounded wait is not offered.
   */
  timeoutMs?: number
  /** Allows test injection. Defaults to global fetch. */
  fetchImpl?: typeof fetch
  /**
   * Intercept specific HTTP statuses before the default error path runs.
   * Return `{ throw: Error }` to substitute a different error, or
   * `{ resolve: T }` to return a typed value without invoking the schema.
   * Return `undefined` to fall through to default behaviour.
   */
  onStatus?: (status: number, responseText: string) => StatusInterceptResult<T> | undefined
}

export type StatusInterceptResult<T> =
  | { throw: Error }
  | { resolve: T }

/**
 * Default deadline for a boundary call, headers through body.
 *
 * A request with no deadline is a silent failure mode: when the upstream
 * stalls, the promise neither resolves nor rejects and the caller's
 * loading state spins forever (observed 2026-08: "Lade Dokument…" hung
 * indefinitely in the engelmann document editor).
 *
 * The value is anchored to the platform ceiling, not to feeling: Vercel
 * cuts our serverless functions at maxDuration = 800s (vercel.json, all
 * apps), so no legitimate response can take longer — 840s adds transfer
 * margin. Callers with tighter latency expectations (interactive loads)
 * pass a smaller `timeoutMs`; there is deliberately NO way to disable
 * the deadline.
 */
export const DEFAULT_HTTP_BOUNDARY_TIMEOUT_MS = 840_000

/**
 * Global handler invoked once per HTTP 401 across the app. Apps install
 * one at boot (typically a top-level layout / shell component) to
 * centralise expired-session UX: clear local state, toast "Sitzung
 * abgelaufen", redirect to /login?expired=1.
 *
 * The handler runs BEFORE the BoundarySourceError is thrown, so the
 * caller still sees its catch fire — but by then the redirect is already
 * queued and any subsequent navigation lands on /login.
 *
 * Re-entrancy: the handler is responsible for its own dedupe (e.g. flag
 * "already redirecting"). httpBoundary calls it once per 401 with no
 * built-in throttling.
 */
export type AuthErrorHandler = (info: {
  name: string
  url: string
  method: string
  responseText: string
}) => void

let _authErrorHandler: AuthErrorHandler | null = null

export function installAuthErrorHandler(handler: AuthErrorHandler): void {
  _authErrorHandler = handler
}

/** Test helper / app teardown. */
export function clearAuthErrorHandler(): void {
  _authErrorHandler = null
}

export async function httpBoundary<T>(options: HttpBoundaryOptions<T>): Promise<T> {
  const {
    name,
    url,
    method = 'GET',
    schema,
    body,
    headers = {},
    credentials = 'same-origin',
    redirect,
    fetchImpl = fetch,
    onStatus,
    timeoutMs = DEFAULT_HTTP_BOUNDARY_TIMEOUT_MS,
  } = options

  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    throw new Error(
      `[trust-boundary] "${name}" invalid timeoutMs ${String(options.timeoutMs)} — must be a positive finite number of milliseconds`,
    )
  }

  const requestHeaders: Record<string, string> = { Accept: 'application/json', ...headers }
  let requestBody: string | undefined
  if (body !== undefined) {
    requestHeaders['Content-Type'] = requestHeaders['Content-Type'] ?? 'application/json'
    requestBody = JSON.stringify(body)
  }

  // The deadline covers the whole exchange: a stalled body stream after
  // 200-headers hangs `response.text()` just as silently as unreachable
  // headers hang `fetch()` — so the timer only stops once the body is in.
  const abort = new AbortController()
  let deadlineHit = false
  const deadlineTimer = setTimeout(() => {
    deadlineHit = true
    abort.abort()
  }, timeoutMs)
  const deadlineError = (cause: unknown) =>
    new BoundarySourceError(
      name,
      `[trust-boundary] "${name}" ${method} ${url} exceeded ${timeoutMs}ms deadline — aborted instead of waiting forever`,
      cause,
    )

  let response: Response
  try {
    response = await fetchImpl(url, {
      method,
      headers: requestHeaders,
      body: requestBody,
      credentials,
      ...(redirect ? { redirect } : {}),
      signal: abort.signal,
    })
  } catch (e) {
    clearTimeout(deadlineTimer)
    if (deadlineHit) throw deadlineError(e)
    throw new BoundarySourceError(
      name,
      `[trust-boundary] "${name}" could not reach ${method} ${url} (network failure)`,
      e,
    )
  }

  let responseText: string
  try {
    responseText = await response.text()
  } catch (e) {
    if (deadlineHit) throw deadlineError(e)
    // Body-read failures other than the deadline stay lenient: status
    // handling below still runs, matching the previous `.catch(() => '')`.
    responseText = ''
  } finally {
    clearTimeout(deadlineTimer)
  }

  if (onStatus) {
    const intercept = onStatus(response.status, responseText)
    if (intercept) {
      if ('throw' in intercept) throw intercept.throw
      return intercept.resolve
    }
  }

  // Global auth-error hook. Apps install a single handler at boot
  // (installAuthErrorHandler) that fires once per 401 to redirect to
  // /login?expired=1. Per-call onStatus still wins for routes that want
  // bespoke 401 handling (e.g. a public-API probe that expects 401 to
  // mean "anonymous user", not "redirect now"). The fallback path below
  // — BoundarySourceError — runs unchanged, so callers that have not
  // installed a handler keep the original behaviour.
  if (response.status === 401 && _authErrorHandler) {
    _authErrorHandler({ name, url, method, responseText })
  }

  if (!response.ok) {
    // `body` stays capped for logs. `json` carries the structured error body
    // UNtruncated: a 409 listing six blocked documents was 854 chars, the
    // capped `body` no longer parsed, and the caller's plain-language message
    // was lost behind the technical line (UX run 30.09.2026, B12).
    throw new BoundarySourceError(
      name,
      `[trust-boundary] "${name}" ${method} ${url} → HTTP ${response.status}`,
      { status: response.status, body: responseText.slice(0, 500), json: parseErrorBody(responseText) },
    )
  }

  // Schema=null means "I expect no body / void return" (DELETE, some POSTs).
  // We still consume the body for fetch hygiene but ignore content.
  if (schema === null) {
    return undefined as T
  }

  if (!responseText) {
    throw new BoundaryParseError(
      name,
      `[trust-boundary] "${name}" expected JSON body but got empty response`,
      null,
      '',
    )
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(responseText)
  } catch (e) {
    throw new BoundaryParseError(
      name,
      `[trust-boundary] "${name}" response is not valid JSON`,
      e,
      responseText,
    )
  }

  return parseAtBoundary(name, schema, parsed)
}

/**
 * Structured error body of a non-OK response, or undefined when the body is
 * empty or not JSON (HTML error page, plain text). Not an error in itself:
 * the capped `body` string stays available next to it.
 */
function parseErrorBody(text: string): unknown {
  if (!text) return undefined
  try {
    return JSON.parse(text)
  } catch {
    // silent-ok: a non-JSON error body is a normal shape here; `body` keeps it.
    return undefined
  }
}
